<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :showFooter="!applyErrorState && !showLoader"
    :footerButtonsParams="footerButtonsParams"
    :scrollValue="scrollValue"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState" class="search-invoices">
                <divider-generic
                :title="t('accounts.invoices.dividerTitle')"
                :applyBorder="false"
                icon="search"
                dividerSize="medium" />
                <div class="dropdown-container">
                    <!-- Customer Number -->
                    <dropdown-generic
                    id="customerNumber"
                    :params="customerNumberParams"
                    :options="customerNumberOptions"
                    :selectedOption="formData.customerNumber"
                    @clicked="[formDataChangeHandler({ field: 'customerNumber', inputValue: $event }), clearFieldError('customerNumber')]"/>
                    <!-- Year -->
                    <dropdown-generic
                    id="year"
                    :params="yearParams"
                    :options="yearOptions"
                    :selectedOption="formData.year"
                    @clicked="[formDataChangeHandler({ field: 'year', inputValue: $event }), clearFieldError('year')]"/>
                    <!-- Begin Date -->
                    <dropdown-generic
                    id="beginDate"
                    :params="beginDateParams"
                    :options="beginDateOptions"
                    :selectedOption="formData.beginDate"
                    @clicked="[formDataChangeHandler({ field: 'beginDate', inputValue: $event }), clearFieldError('beginDate')]"/>
                    <!-- End Date -->
                    <dropdown-generic
                    id="endDate"
                    :params="endDateParams"
                    :options="endOptions"
                    :selectedOption="formData.endDate"
                    @clicked="[formDataChangeHandler({ field: 'endDate', inputValue: $event }), clearFieldError('endDate')]"/>
                </div>
                <transition name="animation-result" appear>
                    <!-- Result search -->
                    <result-search
                    v-if="hasFile"
                    class="animation-result"
                    :formData="formData"
                    :payloadResult="payloadResult"/>
                </transition>
                <transition name="animation-result" appear>
                    <!-- Empty State -->
                    <empty-state-generic
                    v-if="isEmptyState"
                    class="animation-result"
                    illustration="emptyState/search"
                    :title="t('accounts.invoices.emptyState.title')"
                    :description="t('accounts.invoices.emptyState.description')"/>
                </transition>
            </div>
        </transition>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import ResultSearch from '@/components/features/accounts/invoices/ResultSearch'
/* Vue */
import { ref, computed, watch } from 'vue'
/* Assets and Helpers */
import { clearObjectStrings, atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { createJSONDateStartDay, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import {
    monthNames,
    parseLastYears,
    parseCurrentMonthsBeginDate,
    parseMonthsBeginDate,
    parseCurrentMonthsEndDate,
    parseMonthsEndDate
} from '@/assets/scripts/parsers/invoices.js'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {	requestAssociatedCustomers, requestInvoicingDownload } = mapActions(moduleMapper.ACCOUNTS_INVOICES)
const { getAssociatedCustomersList } = mapGetters(moduleMapper.ACCOUNTS_INVOICES)

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const hasFile = ref(null)
const isEmptyState = ref(false)
const applyErrorState = ref(false)
const currentYear = new Date().getFullYear().toString()

/*************************************************
*                                                *
*               Params && Options                *
*                                                *
*************************************************/
/* Customer Number */
const customerNumberParams = ref({
	labelText: t('accounts.invoices.clientLabel'),
    placeholder: t('accounts.invoices.clientPlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const customerNumberOptions = computed(() => {
    return getAssociatedCustomersList.value.map(customer => {
        return {
            id: customer,
            text: customer
        }
    })
})

/* Year */
const yearParams = ref({
	labelText: t('accounts.invoices.yearLabel'),
    placeholder: t('accounts.invoices.yearPlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const yearOptions = computed(() => {
    const today = new Date()

    return parseLastYears(today, 6).map(year => {
        return {
            id: year,
            text: year
        }
    })
})

/* Begin Date */
const beginDateParams = ref({
	labelText: t('accounts.invoices.beginDateLabel'),
    placeholder: t('accounts.invoices.beginDatePlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const getMonthsBeginDate = computed(() => {
    let selectedMonth = formData.value.endDate ? formData.value.endDate : 12
    const selectedYear = formData.value.year ? formData.value.year : new Date().getFullYear()

	if (selectedMonth < 10) selectedMonth = `0${selectedMonth}`

    return currentYear === formData.value.year
        ? parseCurrentMonthsBeginDate(formData.value.endDate)
        : parseMonthsBeginDate(new Date(`${selectedYear}-${selectedMonth}-01`))
})

const beginDateOptions = computed(() => {
    if ((formData.value.year === currentYear && formData.value.endDate === '') || formData.value.endDate !== '') {
        return getMonthsBeginDate.value.map((month) => {
            return {
                id: monthNames.findIndex(el => el === month) + 1,
                text: month
            }
        })
    }

    if (formData.value.endDate === '') {
        return monthNames.map((month, index) => {
            return {
                id: index + 1,
                text: month
            }
        })
    }

    return getMonthsBeginDate.value.map((month) => {
        return {
            id: monthNames.findIndex(el => el === month) + 1,
            text: month
        }
    })
})

/* End Date */
const endDateParams = ref({
	labelText: t('accounts.invoices.endDateLabel'),
    placeholder: t('accounts.invoices.endDatePlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const getMonthsEndDate = computed(() => {
    let selectedMonth = formData.value.beginDate ? formData.value.beginDate : 12
    const selectedYear = formData.value.year ? formData.value.year : new Date().getFullYear()

	if (selectedMonth < 10) selectedMonth = `0${selectedMonth}`

	return currentYear === formData.value.year
        ? parseCurrentMonthsEndDate(formData.value.beginDate)
        : parseMonthsEndDate(new Date(`${selectedYear}-${selectedMonth}-01`))
})

const endOptions = computed(() => {
    if ((formData.value.year === currentYear && formData.value.beginDate === '') || formData.value.beginDate !== '') {
        return getMonthsEndDate.value.map((month) => {
            return {
                id: monthNames.findIndex(el => el === month) + 1,
                text: month
            }
        })
    }

    if (formData.value.beginDate === '') {
        return monthNames.map((month, index) => {
            return {
                id: index + 1,
                text: month
            }
        })
    }

    return getMonthsEndDate.value.map((month) => {
        return {
            id: monthNames.findIndex(el => el === month) + 1,
            text: month
        }
    })
})

/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    return [{
        label: t('accounts.invoices.clearButton'),
        callbackFunction: clearFields,
        buttonPosition: 'left'
    }, {
        label: t('accounts.invoices.searchInvoiceButton'),
        callbackFunction: buttonSearchInvoicesHandler,
        buttonPosition: 'right',
        buttonType: 'primary',
        icon: 'search',
        iconPosition: 'right'
    }]
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const formData = ref({
    customerNumber: '',
    year: '',
    beginDate: '',
    endDate: ''
})

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue

    hasFile.value = false
    isEmptyState.value = false
}

watch(() => currentYear === formData.value.year, () => {
	if ((formData.value.endDate - 1) > new Date().getMonth().toString()) formData.value.endDate = ''
    if ((formData.value.beginDate - 1) > new Date().getMonth()) formData.value.beginDate = ''
})

/**********************************************************************
*                                                                     *
*                MANDATORY FIELDS && CLEAR ERRORS LOGIC               *
*                                                                     *
***********************************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)

const mandatoryList = ref([
    'customerNumber',
    'year',
    'beginDate',
    'endDate'
])

const buttonSearchInvoicesHandler = () => {
    return atLeastOneIsEmpty(formData.value, mandatoryList.value) ? (showMandatoryErrors.value = true) : searchInvoices()
}

const allFormFields = ref({
	customerNumberParams: customerNumberParams.value,
    yearParams: yearParams.value,
    beginDateParams: beginDateParams.value,
    endDateParams: endDateParams.value
})

watch(() => showMandatoryErrors.value, (newValue) => {
	if (newValue) showMandatoryErrorsHandler()
    showMandatoryErrors.value = false
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(formData.value, mandatoryList.value)

	scrollValue.value = getElementOffsetTopPosition(mandatoryFieldsEmptyList[0])

    mandatoryFieldsEmptyList.forEach(fieldName => {
        allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        allFormFields.value[`${fieldName}Params`].errorState = true
    })
}

const clearFieldError = (fieldName) => {
    allFormFields.value[`${fieldName}Params`].errorState = false
    allFormFields.value[`${fieldName}Params`].errorMessage = null
}

const clearFields = () => {
    formData.value = clearObjectStrings(formData.value)

    /* Clear errors */
    Object.keys(allFormFields.value).forEach(field => {
       allFormFields.value[field].errorState = false
       allFormFields.value[field].errorMessage = null
    })

    isEmptyState.value = false
    hasFile.value = false
}

/*************************************************
*                                                *
*                Search Invoices                 *
*                                                *
*************************************************/
const payloadResult = ref()

const searchInvoices = async () => {
    hasFile.value = false
    isEmptyState.value = false
	showOverlayLoader.value = true
    applyErrorState.value = false

	let beginDate = formData.value.beginDate
	let endDate = formData.value.endDate

	if (beginDate < 10) beginDate = `0${beginDate}`
	if (endDate < 10) endDate = `0${endDate}`

    const endDateLastDay = new Date(formData.value.year, endDate, 0).getDate()

    const payload = {
        params: {
            customerNumber: formData.value.customerNumber,
            beginDate: `${formData.value.year}-${beginDate}-01T00:00:00.000Z`,
            endDate: `${formData.value.year}-${endDate}-${endDateLastDay}T23:59:59.999Z`,
            doNotDownload: true
        }
    }

	try {
        await requestInvoicingDownload(payload)

        payloadResult.value = {
            params: {
                customerNumber: formData.value.customerNumber,
				beginDate: `${formData.value.year}-${beginDate}-01T00:00:00.000Z`,
                endDate: `${formData.value.year}-${endDate}-${endDateLastDay}T23:59:59.999Z`
            }
        }

		showOverlayLoader.value = false
        hasFile.value = true
        scrollValue.value = getElementOffsetTopPosition('button')
	} catch (error) {
        showOverlayLoader.value = false
        if (error.response.data.code === 'cpi_connector_error_404') {
            isEmptyState.value = true
            scrollValue.value = getElementOffsetTopPosition('button')
        } else {
            errorHandler(error.response.data, searchInvoices)
        }
	}
}

/*************************************************
*                                                *
*                Initial Requests                *
*                                                *
*************************************************/
const initialRequest = async () => {
    applyErrorState.value = false
	showLoader.value = true

	try {
        await requestAssociatedCustomers()
		showLoader.value = false
	} catch (error) {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequest)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialRequest()
</script>

<style lang="scss" scoped>
.search-invoices {
    @include commonEnteringAnimation;

    width: 100%;

    padding-top: 31px;

    .divider-generic-wrapper {
        margin-bottom: 24px;
    }

    .dropdown-container {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
}

.features-base-content-wrapper .feature-content-container .empty-state-wrapper {
    height: auto;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid var(--secondary-light-2);
    border-radius: 0;
}

.animation-result {
    transition: transform .8s cubic-bezier(0.390, 0.575, 0.565, 1.000), opacity .5s cubic-bezier(0.390, 0.575, 0.565, 1.000) .3s;
    transform: translateY(0px);
    transform-origin: center;

    &.animation-result-enter-active,
    &.animation-result-leave-active {
        opacity: 1;
        transform:translateY(0);
    }

    &.animation-result-leave-active {
        transition: transform .5s cubic-bezier(0.390, 0.575, 0.565, 1.000), opacity .2s cubic-bezier(0.390, 0.575, 0.565, 1.000);
    }

    &.animation-result-enter-from,
    &.animation-result-leave-to {
        opacity: 0;
        transform: translateY(80px);
    }
}
</style>
